import {
	STORAGE_TAPROOT_ADDRESS,
	STORAGE_PUBLIC_KEY,
	STORAGE_WALLET_TYPE,
	NETWORK
} from '../constants'
import SafeLocalStorage from '../safe-local-storage'
import * as btc from '@scure/btc-signer'
import { extractFinalizedPsbtHex } from './psbt'
import Wallet from './'
import { WalletProvider } from '../types/wallet'
import * as bitcoin from 'bitcoinjs-lib'

import { isUserRejection } from './signing-errors'
import {
	beginWalletFlow,
	classifyWalletError,
	createWalletStallWatchdog,
	finishWalletFlow,
	tapProviderDetails,
	type WalletTelemetryError,
	walletErrorDetails,
	walletErrorStage
} from './telemetry'
import type {
	WalletTelemetryErrorCategory,
	WalletTelemetryStage
} from './telemetry-schema'

export const TAP_WALLET_STORE_URL =
	'https://chromewebstore.google.com/detail/tap-wallet/blcaacmeglnfblclocdgaomhopnfobof?hl=en'

// MV3 service workers can take several seconds to wake after Chrome restores a
// tab. Keep the UI responsive with progress messaging, but do not reject a
// valid cold-started provider after only a couple of seconds.
const TAP_PROVIDER_WAIT_MS = 8000
const TAP_PROVIDER_RESPONSE_TIMEOUT_MS = 12_000
// Browser approval prompts are intentionally given much longer than passive
// reads, but they still need a bounded recovery path. A dead MV3 request must
// not disable every wallet option until the page is refreshed.
const TAP_INTERACTIVE_RESPONSE_TIMEOUT_MS = 45_000
const TAP_UNLOCK_SETTLE_MS = 100

type TapSignOptions = {
	autoFinalized?: boolean
	inputForSigns?: Array<{
		index: number
		address?: string
		publicKey?: string
		sighashTypes?: number[]
		disableTweakSigner?: boolean
	}>
}

export interface TapProtocolProvider {
	version?: string
	requestAccounts: () => Promise<string[]>
	getAccounts: () => Promise<string[] | null>
	getPublicKey: () => Promise<string>
	getNetwork: () => Promise<string>
	getVersion?: () => Promise<string>
	signPsbts: (psbts: string[], options?: TapSignOptions[]) => Promise<string[]>
	signPsbt: (psbt: string, options?: TapSignOptions) => Promise<string>
	signMessage: (message: string) => Promise<string>
	on: (event: string, listener: (...args: any[]) => void) => void
	off?: (event: string, listener: (...args: any[]) => void) => void
}

declare global {
	interface Window {
		tapprotocol?: TapProtocolProvider
	}
}

export class TapWalletConnectionError extends Error implements WalletTelemetryError {
	code?: string | number
	telemetryCategory: WalletTelemetryErrorCategory
	telemetryStage: WalletTelemetryStage
	userMessage: string

	constructor(
		message: string,
		options: {
			category: WalletTelemetryErrorCategory
			stage: WalletTelemetryStage
			userMessage: string
			code?: string | number
		}
	) {
		super(message)
		this.name = 'TapWalletConnectionError'
		this.code = options.code
		this.telemetryCategory = options.category
		this.telemetryStage = options.stage
		this.userMessage = options.userMessage
	}
}

const tapError = (
	message: string,
	category: WalletTelemetryErrorCategory,
	stage: WalletTelemetryStage,
	userMessage: string,
	code?: string | number
) => new TapWalletConnectionError(message, { category, stage, userMessage, code })

const tagProviderError = (error: any, stage: WalletTelemetryStage): WalletTelemetryError => {
	if (error?.telemetryCategory && error?.telemetryStage) return error

	const wrapped = new Error(
		String(error?.message ?? error?.error?.message ?? error ?? 'Tap Wallet request failed')
	) as WalletTelemetryError
	wrapped.name = error?.name ?? 'TapWalletProviderError'
	wrapped.code = error?.code ?? error?.error?.code
	wrapped.telemetryStage = stage
	wrapped.telemetryCategory = classifyWalletError(error)
	return wrapped
}

const withTapProviderTimeout = async <T,>(
	request: Promise<T>,
	stage: WalletTelemetryStage,
	userMessage: string,
	timeoutMs: number = TAP_PROVIDER_RESPONSE_TIMEOUT_MS
): Promise<T> => {
	let timeoutTimer: ReturnType<typeof globalThis.setTimeout> | undefined
	try {
		return await Promise.race([
			request,
			new Promise<never>((_resolve, reject) => {
				timeoutTimer = globalThis.setTimeout(() => {
					reject(
						tapError(
							'Tap Wallet provider request timed out',
							'timeout',
							stage,
							userMessage
						)
					)
				}, timeoutMs)
			})
		])
	} finally {
		if (timeoutTimer !== undefined) globalThis.clearTimeout(timeoutTimer)
	}
}

const isValidPublicKey = (publicKey: unknown): publicKey is string => {
	return (
		typeof publicKey === 'string' &&
		(/^[0-9a-f]{64}$/i.test(publicKey) || /^(02|03)[0-9a-f]{64}$/i.test(publicKey))
	)
}

const isMainnetTaprootAddress = (address: unknown): address is string => {
	if (typeof address !== 'string') return false

	try {
		const decoded = bitcoin.address.fromBech32(address)
		return decoded.prefix === 'bc' && decoded.version === 1 && decoded.data.length === 32
	} catch {
		return false
	}
}

const assertMainnet = async (provider: TapProtocolProvider): Promise<void> => {
	let network: string
	try {
		network = String(
			await withTapProviderTimeout(
				provider.getNetwork(),
				'provider_probe',
				'Tap Wallet is installed but not responding. Open it, then refresh this page.'
			)
		).toLowerCase()
	} catch (error) {
		throw tagProviderError(error, 'provider_probe')
	}

	if (network !== 'mainnet') {
		throw tapError(
			`Tap Wallet is using ${network || 'an unknown network'}`,
			'network',
			'provider_probe',
			'Switch Tap Wallet to Bitcoin mainnet, then try again.'
		)
	}
}

const assertProviderContract = (provider: TapProtocolProvider): void => {
	const requiredMethods: Array<keyof TapProtocolProvider> = [
		'requestAccounts',
		'getAccounts',
		'getPublicKey',
		'getNetwork',
		'signPsbt',
		'signPsbts',
		'on'
	]
	const missing = requiredMethods.filter((method) => typeof provider[method] !== 'function')

	if (missing.length > 0) {
		throw tapError(
			`Tap Wallet is missing required methods: ${missing.join(', ')}`,
			'method_missing',
			'provider_probe',
			'Tap Wallet is out of date. Update the extension, refresh this page, and try again.'
		)
	}
}

export const waitForTapProvider = (
	timeoutMs: number = TAP_PROVIDER_WAIT_MS
): Promise<TapProtocolProvider> => {
	if (typeof window === 'undefined') {
		return Promise.reject(
			tapError(
				'Tap Wallet is unavailable outside the browser',
				'provider_missing',
				'provider_probe',
				'Tap Wallet was not detected. Open this page in Chrome with the extension enabled.'
			)
		)
	}

	if (window.tapprotocol) {
		return Promise.resolve(window.tapprotocol)
	}

	return new Promise<TapProtocolProvider>((resolve, reject) => {
		let settled = false
		let pollTimer: number | undefined
		let timeoutTimer: number | undefined

		const cleanup = () => {
			window.removeEventListener('tapprotocol#initialized', onInitialized)
			if (pollTimer !== undefined) window.clearInterval(pollTimer)
			if (timeoutTimer !== undefined) window.clearTimeout(timeoutTimer)
		}

		const finish = (provider?: TapProtocolProvider) => {
			if (settled || !provider) return
			settled = true
			cleanup()
			resolve(provider)
		}

		const onInitialized = () => finish(window.tapprotocol)
		window.addEventListener('tapprotocol#initialized', onInitialized)
		pollTimer = window.setInterval(onInitialized, 50)
		timeoutTimer = window.setTimeout(() => {
			if (settled) return
			settled = true
			cleanup()
				reject(
					tapError(
						'Tap Wallet provider was not injected before the timeout',
						'provider_missing',
						'provider_probe',
						'Tap Wallet was not detected. Enable it for this site, refresh, and try again.'
					)
			)
		}, timeoutMs)
		onInitialized()
	})
}

const readyTapProvider = async (): Promise<TapProtocolProvider> => {
	const provider = await waitForTapProvider()
	assertProviderContract(provider)
	return provider
}

const isHexPsbt = (value: unknown): value is string => {
	return (
		typeof value === 'string' &&
		value.length > 0 &&
		value.length % 2 === 0 &&
		value.toLowerCase().startsWith('70736274ff') &&
		/^[0-9a-f]+$/i.test(value)
	)
}

export default class TapWallet implements WalletProvider {
	private interactiveSessionCheck: Promise<void> | null = null

	get name(): string {
		return 'Tap Wallet'
	}

	get capabilities(): string[] {
		return []
	}

	get connectIcon(): string {
		return '/connect-tap.png'
	}

	get enabled(): boolean {
		return NETWORK === 'BTC'
	}

	async connect(): Promise<boolean> {
		const provider = await readyTapProvider()
		await this.login(provider)
		return true
	}

	async listenForAccountChange(): Promise<void> {
		let provider: TapProtocolProvider
		try {
			provider = await readyTapProvider()
		} catch {
			return
		}

		provider.on('accountsChanged', async (accounts: string[]) => {
			if (Wallet.type() !== 'tap') return

			if (!Array.isArray(accounts) || accounts.length === 0) {
				Wallet.delete()
				window.location.reload()
				return
			}

			try {
				await this.login(provider, accounts)
			} catch {
				Wallet.delete()
			}
			window.location.reload()
		})

		provider.on('networkChanged', (network: string) => {
			if (Wallet.type() !== 'tap' || String(network).toLowerCase() === 'mainnet') return
			Wallet.delete()
			window.location.reload()
		})
	}

	async dummyPsbt(inscription_id: string): Promise<string> {
		const psbt = await Wallet.getEscrow(inscription_id, 1e8 * 21000000, {})
		return psbt
	}

	async login(provider?: TapProtocolProvider, suppliedAccounts?: string[]): Promise<void> {
		const activeProvider = provider ?? (await readyTapProvider())
		await assertMainnet(activeProvider)

		let accounts: string[]
		try {
			accounts =
				suppliedAccounts ??
				(await withTapProviderTimeout(
					activeProvider.requestAccounts(),
					'request_accounts',
					'Tap Wallet did not respond. Close any stale Tap prompt, open or unlock the extension, then try again.',
					TAP_INTERACTIVE_RESPONSE_TIMEOUT_MS
				))
		} catch (error) {
			throw tagProviderError(error, 'request_accounts')
		}

		const address = accounts?.[0]
		if (!isMainnetTaprootAddress(address)) {
			throw tapError(
				'Tap Wallet returned an empty or non-Taproot account',
				'invalid_response',
				'request_accounts',
				'Tap Wallet needs an active Bitcoin Taproot account before it can connect.'
			)
		}

		let publicKey: string
		try {
			publicKey = await withTapProviderTimeout(
				activeProvider.getPublicKey(),
				'get_public_key',
				'Tap Wallet connected but did not return the account key. Open it and try again.'
			)
		} catch (error) {
			throw tagProviderError(error, 'get_public_key')
		}

		if (!isValidPublicKey(publicKey)) {
			throw tapError(
				'Tap Wallet returned an invalid public key',
				'invalid_response',
				'get_public_key',
				'Tap Wallet returned an invalid Bitcoin account. Reopen the extension and try again.'
			)
		}

		// Commit only after the new provider session is fully validated. A rejected or
		// locked Tap attempt must not destroy the user's existing wallet session.
		Wallet.delete()
		localStorage.setItem(STORAGE_TAPROOT_ADDRESS, address)
		localStorage.setItem(STORAGE_WALLET_TYPE, 'tap')
		localStorage.setItem(STORAGE_PUBLIC_KEY, publicKey)
		// The provider session is complete at this point. Profile hydration is a
		// best-effort follow-up and must never keep the connect picker locked if the
		// API is slow or unavailable.
		void Wallet.fetchProfile().catch(() => undefined)
	}

	async assertActiveSession(options: { interactive?: boolean } = {}): Promise<void> {
		if (!options.interactive) {
			return this.verifyActiveSession(false)
		}

		if (!this.interactiveSessionCheck) {
			this.interactiveSessionCheck = this.verifyActiveSession(true).finally(() => {
				this.interactiveSessionCheck = null
			})
		}

		return this.interactiveSessionCheck
	}

	private async verifyActiveSession(interactive: boolean): Promise<void> {
		const provider = await readyTapProvider()
		await assertMainnet(provider)

		let accounts: string[] | null
		try {
			accounts = await withTapProviderTimeout(
				provider.getAccounts(),
				'provider_probe',
				'Tap Wallet is installed but not responding. Open it, then try again.'
			)
		} catch (error) {
			throw tagProviderError(error, 'provider_probe')
		}

		// TAP's safe getAccounts call returns null while the wallet is locked and
		// intentionally does not open its unlock UI. requestAccounts is the
		// interactive equivalent, so let it wake/unlock the wallet before a quote
		// or signature instead of failing before TAP can show anything.
		let unlockedInteractively = false
		if (!accounts?.length && interactive) {
			try {
				accounts = await withTapProviderTimeout(
					provider.requestAccounts(),
					'request_accounts',
					'Tap Wallet did not respond. Close any stale Tap prompt, open or unlock the extension, then try again.',
					TAP_INTERACTIVE_RESPONSE_TIMEOUT_MS
				)
				unlockedInteractively = true
			} catch (error) {
				throw tagProviderError(error, 'request_accounts')
			}
		}

		// Quotes can safely use the stored account that was validated at connect.
		// A locked extension returns no passive accounts; signing performs the
		// interactive check before any PSBT is disclosed to the provider.
		if (!accounts?.length && !interactive) return

		const activeAddress = accounts?.[0]
		const storedAddress = Wallet.getTaprootAddress()
		if (!isMainnetTaprootAddress(activeAddress) || activeAddress !== storedAddress) {
			throw tapError(
				'Tap Wallet active account does not match the connected account',
				'invalid_response',
				'provider_probe',
				'Tap Wallet changed accounts. Reconnect it before continuing.'
			)
		}

		if (unlockedInteractively) {
			// requestAccounts resolves as the unlock popup closes, just before Tap
			// releases its approval lock. Let that request finish before signing.
			await new Promise<void>((resolve) => globalThis.setTimeout(resolve, TAP_UNLOCK_SETTLE_MS))
		}
	}

	taprootAddress(): string {
		return SafeLocalStorage.getItem(STORAGE_TAPROOT_ADDRESS)
	}

	private async runSigning<T>(
		stage: Extract<
			WalletTelemetryStage,
			'sign_psbt' | 'sign_psbts' | 'sign_setup' | 'sign_purchase'
		>,
		operation: () => Promise<T>
	): Promise<T> {
		const flow = beginWalletFlow('wallet_sign', 'tap', 'tap_adapter', stage, {
			...tapProviderDetails()
		})
		const clearWatchdog = createWalletStallWatchdog(flow, stage)

		try {
			await this.assertActiveSession({ interactive: true })
			const result = await operation()
			finishWalletFlow(flow, stage, 'success', tapProviderDetails())
			return result
		} catch (error) {
			const category = classifyWalletError(error)
			const errorStage = walletErrorStage(error)
			finishWalletFlow(
				flow,
				errorStage === 'unknown' ? stage : errorStage,
				category === 'user_rejected' ? 'cancelled' : 'failure',
				{ ...tapProviderDetails(), ...walletErrorDetails(error) }
			)
			throw error
		} finally {
			clearWatchdog()
		}
	}

	async signPSBTS(psbts: string[]): Promise<string[]> {
		return this.runSigning('sign_psbts', async () => {
			const provider = await readyTapProvider()
			// TAP auto-detects owned inputs. Explicitly marking every input would also
			// include seller/escrow inputs in collaborative purchase PSBTs.
			const options = psbts.map(() => ({ autoFinalized: true }))
			const signed = await withTapProviderTimeout(
				provider.signPsbts(psbts, options),
				'sign_psbts',
				'Tap Wallet did not finish signing. Close any stale Tap prompt, unlock it, and try again.',
				TAP_INTERACTIVE_RESPONSE_TIMEOUT_MS
			)
			if (!Array.isArray(signed) || signed.length !== psbts.length || !signed.every(isHexPsbt)) {
				throw tapError(
					'Tap Wallet returned invalid signed PSBTs',
					'invalid_response',
					'sign_psbts',
					'Tap Wallet returned an invalid signature response. Update the extension and try again.'
				)
			}
			return signed
		})
	}

	async signPSBT(psbt: string): Promise<string> {
		return this.runSigning('sign_psbt', async () => {
			const provider = await readyTapProvider()
			const signed = await withTapProviderTimeout(
				provider.signPsbt(psbt, { autoFinalized: true }),
				'sign_psbt',
				'Tap Wallet did not finish signing. Close any stale Tap prompt, unlock it, and try again.',
				TAP_INTERACTIVE_RESPONSE_TIMEOUT_MS
			)
			if (!isHexPsbt(signed)) {
				throw tapError(
					'Tap Wallet returned an invalid signed PSBT',
					'invalid_response',
					'sign_psbt',
					'Tap Wallet returned an invalid signature response. Update the extension and try again.'
				)
			}
			return signed
		})
	}

	async signPSBTExtractTx(psbt: string): Promise<string> {
		return this.runSigning('sign_psbt', async () => {
			const provider = await readyTapProvider()
			const signed = await withTapProviderTimeout(
				provider.signPsbt(psbt, { autoFinalized: true }),
				'sign_psbt',
				'Tap Wallet did not finish signing. Close any stale Tap prompt, unlock it, and try again.',
				TAP_INTERACTIVE_RESPONSE_TIMEOUT_MS
			)
			if (!isHexPsbt(signed)) {
				throw tapError(
					'Tap Wallet returned an invalid signed PSBT',
					'invalid_response',
					'sign_psbt',
					'Tap Wallet returned an invalid signature response. Update the extension and try again.'
				)
			}
			return extractFinalizedPsbtHex(signed)
		})
	}

	async signMessage(message: string): Promise<string> {
		try {
			const provider = await readyTapProvider()
			return await provider.signMessage(message)
		} catch (error) {
			if (isUserRejection(error)) return ''
			throw error
		}
	}

	async signInscribe(psbt: string): Promise<string> {
		return this.signPSBTExtractTx(psbt)
	}

	async signTransfer(psbt: string): Promise<string> {
		return this.signPSBTExtractTx(psbt)
	}

	async signEscrow(psbt: string): Promise<string> {
		return this.signPSBT(psbt)
	}

	async signPurchase(
		setup_psbt: string,
		purchase_psbt: string
	): Promise<{ signedSetup: string; signedPurchase: string }> {
		try {
			let signedSetup = ''

			if (setup_psbt) {
				signedSetup = await this.runSigning('sign_setup', async () => {
					const provider = await readyTapProvider()
					const signedSetupPsbt = await withTapProviderTimeout(
						provider.signPsbt(setup_psbt, { autoFinalized: true }),
						'sign_setup',
						'Tap Wallet did not finish signing. Close any stale Tap prompt, unlock it, and try again.',
						TAP_INTERACTIVE_RESPONSE_TIMEOUT_MS
					)
					if (!isHexPsbt(signedSetupPsbt)) {
						throw tapError(
							'Tap Wallet returned an invalid setup signature',
							'invalid_response',
							'sign_setup',
							'Tap Wallet returned an invalid setup signature. Update it and try again.'
						)
					}
					const setupTransaction = btc.Transaction.fromPSBT(
						Buffer.from(signedSetupPsbt, 'hex')
					)
					return Buffer.from(setupTransaction.extract()).toString('hex')
				})
			}

			const signedPurchase = await this.runSigning('sign_purchase', async () => {
				const provider = await readyTapProvider()
				const signed = await withTapProviderTimeout(
					provider.signPsbt(purchase_psbt, { autoFinalized: true }),
					'sign_purchase',
					'Tap Wallet did not finish signing. Close any stale Tap prompt, unlock it, and try again.',
					TAP_INTERACTIVE_RESPONSE_TIMEOUT_MS
				)
				if (!isHexPsbt(signed)) {
					throw tapError(
						'Tap Wallet returned an invalid purchase signature',
						'invalid_response',
						'sign_purchase',
						'Tap Wallet returned an invalid purchase signature. Update it and try again.'
					)
				}
				return signed
			})

			return { signedSetup, signedPurchase }
		} catch (error) {
			if (isUserRejection(error)) {
				return undefined as unknown as { signedSetup: string; signedPurchase: string }
			}
			throw error
		}
	}
}
